//@ts-nocheck
import { useState } from 'react';
import { useActor } from '@xstate/react';
import Timer from 'react-compound-timer/build';
import { useTranslation } from 'react-i18next';
import { useFormApi } from 'informed';

import {
  CLEAR_OTP_ERROR,
  events,
  OTP_LOADING,
  otpFieldConstants,
  RESEND_OTP_CLICKED,
  RESEND_OTP_THROUGH_CALL_CLICKED,
  RESENDING_OTP_TAG,
  states,
  VERIFY_OTP_BUTTON_CLICKED
} from '@nw/shared/config';

import { MessageIcon } from '../../icons/MessageIcon';

import { InformedOTPFieldWithBoxes } from '../InformedOTPBoxesField';
import { Loader } from '../Loader';

import {
  errorMessageStylesCss,
  IconWrapper,
  OrText,
  OTPBoxesFieldContainer,
  OTPFieldContainer,
  OTPTimerText,
  ResendButton,
  ResendOTPAndCallContainer,
  ResendOtpButtonAwayFromTimer,
  ResendOtpButtonAwayFromTimerContainer,
  ResendOTPText,
  ResendOTPTextContainer,
  ResendOTPWithOTPTimerContainer,
  TimeUp
} from './styledComponents';

interface OTPFieldWithResendProps {
  field: string;
  validationSchemaObject: any;
  numInputs?: number;
  state: any;
  formState: any;
  inputClassName?: string;
  inputFocusClassName?: string;
  errorClassName?: string;
  errorMessageStyles?: any;
  containerStyle?: string;
  labelStyles?: string;
  label?: string;
  disabled?: boolean;
  otpFieldContainerStyles?: string;
  OTPBoxesFieldContainerStyles?: string;
  otpContainerStyles?: string;
  resendOtpButtonWithTimerVariant?: string;
  customClassNames?: any;
  renderAgreeTermsAndConditionsCheckbox?: () => React.ReactElement | null;
  enableAutoVerify?: boolean;
}

const OTPFieldWithResend = (props: OTPFieldWithResendProps) => {
  const {
    field,
    validationSchemaObject,
    inputClassName,
    inputFocusClassName,
    errorClassName,
    errorMessageStyles,
    numInputs = 6,
    disabled = false,
    containerStyle,
    formState,
    label = '',
    labelStyles,
    state,
    otpFieldContainerStyles,
    OTPBoxesFieldContainerStyles,
    otpContainerStyles,
    resendOtpButtonWithTimerVariant = otpFieldConstants.timerWithResendOtp,
    customClassNames = {},
    renderAgreeTermsAndConditionsCheckbox,
    enableAutoVerify = false
  } = props;

  const {
    resendOtpButtonAwayFromTimer = 'resend-otp-button-away-from-timer',
    OTPTimerTextClassName = 'OTPTimerTextClassName'
  } = customClassNames;

  const { touched, errors } = formState;

  const [isTimeOut, setIsTimeOut] = useState(false);
  const timer = 30;

  const { t } = useTranslation();
  const formApi = useFormApi();

  const {
    context: { otpFieldRef }
  } = state;

  const [otpFieldState, otpFieldSend] = useActor(otpFieldRef);

  const {
    context: { otpStateRef }
  } = otpFieldState;

  const [otpState, otpStateSend] = useActor(otpStateRef);

  const isResendOTPEnabled =
    otpState?.matches(states.RESEND_ENABLED_STATE) &&
    !otpFieldState.hasTag(RESENDING_OTP_TAG);

  const toggleOtpTimeout = () => {
    setIsTimeOut(!isTimeOut);
  };

  const toggleOtpTimeoutCallBack = () => {
    setIsTimeOut(!isTimeOut);

    otpStateSend({ type: events.RESEND_ENABLE });
  };

  const resetTimer = (reset, start) => {
    toggleOtpTimeout();
    reset();
    start();
  };

  const onClickResend = (reset, start) => () => {
    // Reset form value
    formApi.setValue(field, '');
    // Reset touched state
    formApi.setTouched(field, false);
    // Clear form errors
    formApi.setError(field, undefined);
    const triggerResetTimer = () => {
      resetTimer(reset, start);
    };
    otpFieldSend({ type: RESEND_OTP_CLICKED, resetTimer: triggerResetTimer });
  };

  const isLoading =
    state.hasTag(OTP_LOADING) || otpFieldState.hasTag(OTP_LOADING);

  const verifyOTPOnSubmit = (otpValue: string) => {
    otpFieldSend({
      type: VERIFY_OTP_BUTTON_CLICKED,
      values: { otp: otpValue }
    });
  };

  const getResendOtpButtonAwayFromTimer = (reset, start) => (
    <ResendOtpButtonAwayFromTimer
      type="button"
      disabled={!isResendOTPEnabled}
      onClick={!isLoading && onClickResend(reset, start)}
      className={
        isResendOTPEnabled
          ? resendOtpButtonAwayFromTimer
          : OTPTimerTextClassName
      }
    >
      {t('common.resendOTP')}
    </ResendOtpButtonAwayFromTimer>
  );

  const getResetOtpButtonAwayFromTimerContainer = (reset, start) => {
    return (
      <ResendOtpButtonAwayFromTimerContainer>
        {getResendOtpButtonAwayFromTimer(reset, start)}
        {!isResendOTPEnabled && !otpFieldState.hasTag(RESENDING_OTP_TAG) ? (
          <OTPTimerText className={OTPTimerTextClassName}>
            <Timer.Seconds /> {t('common.seconds')}
          </OTPTimerText>
        ) : (
          <TimeUp>{`00:00`}</TimeUp>
        )}
      </ResendOtpButtonAwayFromTimerContainer>
    );
  };

  const getResendOTPButton = (reset, start) => (
    <ResendOTPWithOTPTimerContainer>
      <ResendButton type={'button'} disabled={!isResendOTPEnabled}>
        {otpFieldState.hasTag(RESENDING_OTP_TAG) ? (
          <Loader />
        ) : !isResendOTPEnabled ? (
          <ResendOTPText disabled={!isResendOTPEnabled}>
            {t('common.resendOTPIn')}
          </ResendOTPText>
        ) : (
          <ResendOTPAndCallContainer>
            <ResendOTPTextContainer
              isDisabled={isLoading}
              onClick={!isLoading && onClickResend(reset, start)}
            >
              <IconWrapper>
                <MessageIcon />
              </IconWrapper>
              <ResendOTPText>{t('common.resendOTP')}</ResendOTPText>
            </ResendOTPTextContainer>
          </ResendOTPAndCallContainer>
        )}
      </ResendButton>
      {!isResendOTPEnabled && !otpFieldState.hasTag(RESENDING_OTP_TAG) ? (
        <OTPTimerText>
          <Timer.Seconds /> {t('common.seconds')}
        </OTPTimerText>
      ) : null}
    </ResendOTPWithOTPTimerContainer>
  );

  const getResendOtpButtonWithTimer = (
    resendOtpButtonWithTimerVariant,
    reset,
    start
  ) => {
    switch (resendOtpButtonWithTimerVariant) {
      case otpFieldConstants.timerAwayFromResendOtp: {
        return getResetOtpButtonAwayFromTimerContainer(reset, start);
      }
      case otpFieldConstants.timerWithResendOtp: {
        return getResendOTPButton(reset, start);
      }
      default:
        return getResendOTPButton(reset, start);
    }
  };

  return (
    <Timer
      initialTime={timer * 1000}
      direction="backward"
      formatValue={(value) => `${value < 10 ? `0${value}` : value}`}
      checkpoints={[
        {
          time: 0,
          callback: () => toggleOtpTimeoutCallBack()
        }
      ]}
    >
      {({ reset, start, stop }) => (
        <OTPFieldContainer css={otpFieldContainerStyles}>
          <OTPBoxesFieldContainer css={OTPBoxesFieldContainerStyles}>
            <InformedOTPFieldWithBoxes
              disabled={disabled || otpFieldState.hasTag(OTP_LOADING)}
              field={field}
              validationSchemaObject={validationSchemaObject}
              touched={touched[field]}
              error={errors[field] || otpFieldState.context.errorMessage}
              errorMessageStyles={
                errorMessageStyles ? errorMessageStyles : errorMessageStylesCss
              }
              numInputs={numInputs}
              inputClassName={inputClassName ? inputClassName : 'otp-input'}
              inputFocusClassName={
                inputFocusClassName ? inputFocusClassName : 'otp-input-focus'
              }
              errorClassName={errorClassName ? errorClassName : 'otp-error'}
              label={label}
              containerStyle={
                containerStyle ? containerStyle : 'container-styles'
              }
              labelStyles={labelStyles}
              otpContainerStyles={otpContainerStyles}
              enableAutoVerify={enableAutoVerify}
              onVerifyOTP={enableAutoVerify ? verifyOTPOnSubmit : undefined}
              onClearError={() => otpFieldSend({ type: CLEAR_OTP_ERROR })}
            />
          </OTPBoxesFieldContainer>
          {renderAgreeTermsAndConditionsCheckbox &&
            renderAgreeTermsAndConditionsCheckbox()}
          {getResendOtpButtonWithTimer(
            resendOtpButtonWithTimerVariant,
            reset,
            start
          )}
        </OTPFieldContainer>
      )}
    </Timer>
  );
};

export { OTPFieldWithResend };
